import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, buildWaUrl } from "../data/site";
import { BoltIcon } from "./Decorations";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/layanan", label: "Layanan" },
  { to: "/tentang", label: "Tentang" },
  { to: "/blog", label: "Blog" },
  { to: "/kontak", label: "Kontak" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const darkHero =
    pathname === "/" || pathname.startsWith("/jasa-listrik/");
  const onDark = darkHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            data-testid="logo-link"
          >
            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-black overflow-hidden shadow-md">
              <img
                src={SITE.logo}
                alt="ListrikBatam.com Logo"
                className="w-full h-full object-cover"
              />
            </span>
            <span className="flex flex-col leading-tight">
              <span className={`font-display font-extrabold text-lg tracking-tight transition-colors ${onDark ? "text-white" : "text-[#0F4C75]"}`}>
                Listrik<span className="text-[#FF6B00]">Batam</span>
                <span className={onDark ? "text-[#FFB341]" : "text-[#3282B8]"}>.com</span>
              </span>
              <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors ${onDark ? "text-white/70" : "text-[#1B262C]/60"}`}>
                Professional Electrical Service
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav
            className={`hidden lg:flex items-center gap-8 text-sm font-semibold transition-colors ${onDark ? "text-white" : "text-[#1B262C]"}`}
            aria-label="Primary"
          >
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link transition-colors ${onDark ? "hover:text-[#FFB341]" : "hover:text-[#0F4C75]"} ${
                    isActive ? (onDark ? "active text-[#FFB341]" : "active text-[#0F4C75]") : ""
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:+${SITE.whatsappNumber}`}
              data-testid="header-phone-btn"
              className={`hidden xl:inline-flex items-center gap-2 text-sm font-semibold transition-colors ${onDark ? "text-white hover:text-[#FFB341]" : "text-[#1B262C] hover:text-[#0F4C75]"}`}
            >
              <Phone className="w-4 h-4" /> +62 811-7006-5656
            </a>
            <a
              href={buildWaUrl("Halo, saya ingin konsultasi jasa listrik di Batam.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-wa-cta"
              className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full px-5 py-2.5 text-sm shadow-[0_6px_18px_rgba(255,107,0,0.35)] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(255,107,0,0.45)] transition-all"
            >
              <BoltIcon className="w-3.5 h-4" color="#fff" />
              Chat Teknisi
            </a>
          </div>

          {/* Mobile trigger */}
          <button
            onClick={() => setOpen(true)}
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${onDark ? "border-white/30 bg-white/10 text-white" : "border-[#E5E7EB] bg-white/70 text-[#0F4C75]"}`}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#1B262C]/60 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-overlay"
        >
          <div
            className="absolute top-0 right-0 h-full w-[82%] max-w-sm bg-white p-6 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-display font-extrabold text-[#0F4C75] text-lg">
                Listrik<span className="text-[#FF6B00]">Batam</span>
              </span>
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 rounded-lg bg-[#F8FAFC] text-[#0F4C75]"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5 mx-auto" />
              </button>
            </div>

            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-semibold transition ${
                      isActive
                        ? "bg-[#0F4C75] text-white"
                        : "text-[#1B262C] hover:bg-[#F8FAFC]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="mt-auto flex flex-col gap-3 pt-6">
              <a
                href={buildWaUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-xl px-5 py-3.5 shadow-md"
                data-testid="mobile-wa-cta"
              >
                Chat WhatsApp
              </a>
              <a
                href={`tel:+${SITE.whatsappNumber}`}
                className="inline-flex justify-center items-center gap-2 border border-[#E5E7EB] text-[#0F4C75] font-semibold rounded-xl px-5 py-3.5"
              >
                <Phone className="w-4 h-4" /> +62 811-7006-5656
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
